// Copy for the shared "Why Traditional Retail Data Fails" section
// (src/components/WhyRetailDataFailsSection.tsx — used on RAS and Retail Profit Management).

const EN_COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'];

const en = {
  headingPrefix: 'Why Traditional Retail Data ',
  headingAccent: 'Fails',
  /** Intro sentence; the problem count is derived from problems.length. */
  intro: (count: number) =>
    `Our analysis of the market reveals ${EN_COUNT_WORDS[count] ?? String(count)} core problems holding retailers back:`,
  problems: [
    {
      title: 'Lack of a North Star',
      text: 'Most retailers track dozens of metrics but lack priority. When asked for their ultimate goal, many say \'Revenues.\' But chasing revenue blindly—by increasing marketing spend or slashing prices—often destroys profitability. You need a system that ruthlessly prioritises Net Profit.',
    },
    {
      title: 'Data Inertia',
      text: 'Companies often believe that having a report means the data job is done. But a report is passive; it sleeps when you sleep. True data-driven success comes only when your data works for you 24/7, actively triggering decisions and workflows.',
    },
    {
      title: 'AI Cannot Access Data',
      text: 'Even though we live in the age of AI, most companies still use it just to generate text and pictures. The true power of AI inference can be unleashed when you connect it to your core dataset. But AI\'s answers are only as good as the data behind them, which is why that data has to be kept clean and reliable.',
    },
    {
      title: 'Fragmented Automation',
      text: 'Retailers often buy separate \'box solutions\' for different problems—one tool for dynamic pricing, another for stock management, and a third for bidding. This leads to fragmented data, multiple invoices, and disconnected strategies, even though they all rely on the same underlying data.',
    },
  ],
  cta: 'Tell me more',
};

// Slovak numerals for "problém" (masculine inanimate): 1 → jeden problém, 2–4 → problémy, 5+ → problémov.
const SK_COUNT_WORDS = ['nula', 'jeden', 'dva', 'tri', 'štyri', 'päť', 'šesť', 'sedem', 'osem'];

const sk: typeof en = {
  headingPrefix: 'Prečo tradičné retailové dáta ',
  headingAccent: 'zlyhávajú',
  intro: (count: number) => {
    const word = SK_COUNT_WORDS[count] ?? String(count);
    if (count === 1) return `Naša analýza trhu odhalila ${word} zásadný problém, ktorý brzdí retailerov:`;
    if (count >= 2 && count <= 4) return `Naša analýza trhu odhalila ${word} zásadné problémy, ktoré brzdia retailerov:`;
    return `Naša analýza trhu odhalila ${word} zásadných problémov, ktoré brzdia retailerov:`;
  },
  problems: [
    {
      title: 'Chýba North Star metrika',
      text: 'Väčšina retailerov sleduje desiatky metrík, no chýba im priorita. Keď sa ich spýtate na hlavný cieľ, mnohí povedia „tržby“. Lenže slepá honba za tržbami, vyšším marketingovým rozpočtom či zľavami často zničí ziskovosť. Potrebujete systém, ktorý bez kompromisov uprednostňuje čistý zisk.',
    },
    {
      title: 'Dáta, ktoré len ležia',
      text: 'Firmy si často myslia, že keď majú report, s dátami je hotovo. Lenže report je pasívny. Spí, keď spíte vy. Skutočný úspech s dátami prichádza až vtedy, keď pre vás dáta pracujú 24/7 a samy spúšťajú rozhodnutia a procesy.',
    },
    {
      title: 'AI sa k dátam nedostane',
      text: 'Hoci žijeme v ére AI, väčšina firiem ju stále používa len na generovanie textov a obrázkov. AI ukáže, čo naozaj dokáže, až keď ju napojíte na svoje hlavné dáta. Jej odpovede sú však len také kvalitné, aké kvalitné sú vaše dáta. Preto ich treba udržiavať čisté a spoľahlivé.',
    },
    {
      title: 'Roztrieštená automatizácia',
      text: 'Retaileri si na rôzne problémy často kupujú samostatné krabicové riešenia. Jeden nástroj na dynamickú cenotvorbu, ďalší na riadenie zásob, tretí na bidovanie v PPC. Dáta sú potom roztrieštené, faktúr pribúda a stratégie spolu nesúvisia, hoci všetky tieto nástroje stoja na tých istých dátach.',
    },
  ],
  cta: 'Chcem vedieť viac',
};

export const whyRetailDataFailsCopy = { en, sk };
